/**
 * A única fronteira entre "horário que a barbearia enxerga" e "instante que o
 * banco guarda".
 *
 * Regras do projeto:
 *  - todo instante persistido é UTC;
 *  - horário de trabalho é minuto local (0..1440), porque "abro às 9h" não muda
 *    quando o fuso muda;
 *  - nenhuma função de agenda usa o fuso do servidor. Se um teste passa aqui e
 *    falha em produção, é porque alguém leu o relógio errado.
 */

import { TZDate } from '@date-fns/tz';

/** Data no calendário da barbearia, sem fuso e sem hora: 'AAAA-MM-DD'. */
export type CalendarDate = string;

const CALENDAR_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

export interface CalendarParts {
  year: number;
  month: number; // 1..12
  day: number; // 1..31
}

export function parseCalendarDate(date: CalendarDate): CalendarParts {
  if (!CALENDAR_DATE_RE.test(date)) {
    throw new RangeError(`Data inválida: "${date}". Esperado AAAA-MM-DD.`);
  }
  const [year, month, day] = date.split('-').map(Number) as [number, number, number];
  return { year, month, day };
}

function pad(value: number, size = 2): string {
  return String(value).padStart(size, '0');
}

export function formatCalendarDate({ year, month, day }: CalendarParts): CalendarDate {
  return `${pad(year, 4)}-${pad(month)}-${pad(day)}`;
}

/**
 * Instante UTC correspondente a um minuto do dia no fuso da barbearia.
 *
 * `minutes` pode ser 1440 (meia-noite do dia seguinte), que é como uma janela
 * de trabalho representa "fecho à meia-noite".
 */
export function zonedMinutesToInstant(date: CalendarDate, minutes: number, timezone: string): Date {
  const { year, month, day } = parseCalendarDate(date);
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  // O construtor normaliza sozinho hora >= 24, virando o dia.
  return new Date(TZDate.tz(timezone, year, month - 1, day, hours, rest, 0, 0).getTime());
}

/** Minutos desde a meia-noite local (0..1439) de um instante. */
export function instantToZonedMinutes(instant: Date, timezone: string): number {
  const zoned = new TZDate(instant, timezone);
  return zoned.getHours() * 60 + zoned.getMinutes();
}

/** Em que dia do calendário da barbearia este instante cai. */
export function instantToCalendarDate(instant: Date, timezone: string): CalendarDate {
  const zoned = new TZDate(instant, timezone);
  return formatCalendarDate({
    year: zoned.getFullYear(),
    month: zoned.getMonth() + 1,
    day: zoned.getDate(),
  });
}

/**
 * Dia da semana de uma data de calendário: 0 = domingo … 6 = sábado.
 *
 * Independe de fuso — uma data de calendário já é local por definição — então
 * calculamos em UTC de propósito, para não haver deslocamento.
 */
export function weekdayOf(date: CalendarDate): number {
  const { year, month, day } = parseCalendarDate(date);
  return new Date(Date.UTC(year, month - 1, day)).getUTCDay();
}

export function addCalendarDays(date: CalendarDate, days: number): CalendarDate {
  const { year, month, day } = parseCalendarDate(date);
  const shifted = new Date(Date.UTC(year, month - 1, day + days));
  return formatCalendarDate({
    year: shifted.getUTCFullYear(),
    month: shifted.getUTCMonth() + 1,
    day: shifted.getUTCDate(),
  });
}

export function differenceInCalendarDays(later: CalendarDate, earlier: CalendarDate): number {
  const a = parseCalendarDate(later);
  const b = parseCalendarDate(earlier);
  const msA = Date.UTC(a.year, a.month - 1, a.day);
  const msB = Date.UTC(b.year, b.month - 1, b.day);
  return Math.round((msA - msB) / 86_400_000);
}

/** 'HH:mm' no fuso da barbearia. */
export function formatZonedTime(instant: Date, timezone: string): string {
  const zoned = new TZDate(instant, timezone);
  return `${pad(zoned.getHours())}:${pad(zoned.getMinutes())}`;
}

/** 'HH:mm' a partir de minutos desde a meia-noite. */
export function formatMinutesOfDay(minutes: number): string {
  return `${pad(Math.floor(minutes / 60))}:${pad(minutes % 60)}`;
}

/** 'HH:mm' → minutos desde a meia-noite. */
export function parseMinutesOfDay(value: string): number {
  const match = /^(\d{1,2}):(\d{2})$/.exec(value);
  if (!match) throw new RangeError(`Horário inválido: "${value}". Esperado HH:mm.`);
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours > 24 || minutes > 59 || (hours === 24 && minutes > 0)) {
    throw new RangeError(`Horário fora do dia: "${value}".`);
  }
  return hours * 60 + minutes;
}

export function isValidTimezone(timezone: string): boolean {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: timezone });
    return true;
  } catch {
    return false;
  }
}
